<template>
  <div>
    <v-container class="toggle animated" fluid>
      <v-row dense>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="search"
            variant="outlined"
            prepend-inner-icon="mdi-magnify"
            :label="$t('TableHeaders.Search')"
            density="comfortable"
            solo
            hide-details
            class="rounded-lg"
            color="white"
            style="
              font-family:
                Saira Condensed,
                sans-serif;
            "
          ></v-text-field>
        </v-col>
        <v-col class="text-right" cols="12" md="8">
          <v-btn
            @click="showAddDialog()"
            prepend-icon="mdi-plus"
            class="mt-2 rounded-lg"
            color="primary"
            height="40px"
            elevation="2"
            style="
              font-family:
                Saira Condensed,
                sans-serif;
            "
          >
            {{ $t("GuessTheBalance.Actions.AddGTB") }}
          </v-btn>
        </v-col>
        <v-col cols="12">
          <v-card class="dt-container mt-2">
            <v-data-table
              :mobile="isMobileOrTablet()"
              :headers="headers"
              :items="gtbData"
              :search="search"
              :loading="loading"
              class="text-subtitle-1"
              style="
                font-family:
                  Saira Condensed,
                  sans-serif;
              "
            >
              <template v-slot:loading>
                <v-progress-linear
                  indeterminate
                  color="primary"
                  height="5"
                  class="mt-1 rounded-lg"
                ></v-progress-linear>
                <!-- <v-skeleton-loader type="table-row@10"></v-skeleton-loader> -->
              </template>
              <template v-slot:[`item.status`]="{ item }">
                <span v-if="item.status == 'active'">
                  <v-chip color="blue">{{
                    $t("GuessTheBalance.Status.Active")
                  }}</v-chip>
                </span>
                <span v-if="item.status == 'done'">
                  <v-chip color="gray">{{
                    $t("GuessTheBalance.Status.Done")
                  }}</v-chip>
                </span>
                <span v-if="item.status == 'started'">
                  <v-chip color="green">{{
                    $t("GuessTheBalance.Status.StartedGuessing")
                  }}</v-chip>
                </span>
                <span v-if="item.status == 'ongoing'">
                  <v-chip color="orange">{{
                    $t("GuessTheBalance.Status.Ongoing")
                  }}</v-chip>
                </span>
              </template>

              <template v-slot:[`item.createdAt`]="{ item }">
                <span>{{ formatDateTime(item.createdAt) }}</span>
              </template>

              <template v-slot:[`item.startingBalance`]="{ item }">
                <span>{{
                  item.startingBalance > 0 ? "$" + item.startingBalance : ""
                }}</span>
              </template>

              <template v-slot:[`item.endingBalance`]="{ item }">
                <span>{{
                  item.endingBalance > 0 ? "$" + item.endingBalance : ""
                }}</span>
              </template>

              <template v-slot:[`item.profit`]="{ item }">
                <span>{{ item.profit > 0 ? "$" + item.profit : "" }}</span>
              </template>

              <template v-slot:[`item.timerDuration`]="{ item }">
                <span v-if="item.status == 'done'">--:--</span>
                <span v-else
                  >{{ String(timer(item).minutes.value).padStart(2, "0") }} :
                  {{ String(timer(item).seconds.value).padStart(2, "0") }}</span
                >
              </template>

              <template v-slot:[`item.action`]="{ item }">
                <v-icon
                  v-if="item.status != 'active'"
                  @click="viewBalanceDetail(item)"
                  class="me-2"
                  size="small"
                >
                  mdi-trophy
                </v-icon>
                <v-icon @click="viewSlots(item)" class="me-2" size="small">
                  mdi-list-box
                </v-icon>
                <v-icon
                  v-if="item.status == 'active'"
                  @click="showEditDialog(item)"
                  class="me-2"
                  size="small"
                >
                  mdi-pencil
                </v-icon>
                <v-icon
                  v-if="item.status == 'done'"
                  @click="showDeleteDialog(item)"
                  class="me-2"
                  size="small"
                >
                  mdi-delete
                </v-icon>
                <v-icon
                  v-if="
                    item.status == 'started' &&
                    !timerHasStarted &&
                    timer(item).minutes.value == 0 &&
                    timer(item).seconds.value == 0
                  "
                  @click="showPlayDialog(item)"
                  class="me-2"
                  size="small"
                >
                  mdi-play-circle
                </v-icon>

                <v-tooltip
                  :text="$t('GuessTheBalance.Messages.OpenSlotsWarning')"
                  location="top"
                >
                  <template v-slot:activator="{ props }">
                    <v-icon
                      v-if="item.status != 'active' && hasOpenSlots(item)"
                      v-bind="props"
                      class="me-2 text-warning"
                      size="small"
                    >
                      mdi-progress-clock
                    </v-icon>
                  </template>
                </v-tooltip>

                <v-icon
                  v-if="item.status == 'ongoing' && !hasOpenSlots(item)"
                  @click="showDoneDialog(item)"
                  class="me-2"
                  size="small"
                >
                  mdi-stop-circle-outline
                </v-icon>

                <v-icon
                  v-if="item.status == 'active'"
                  @click="startTimer(item)"
                  class="me-2"
                  size="small"
                >
                  mdi-timer
                </v-icon>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog
      v-model="startDialog"
      width="450"
      persistent
      style="
        font-family:
          Saira Condensed,
          sans-serif;
      "
    >
      <v-card color="white" rounded="xl" class="pa-6" elevation="10">
        <!-- Warning Banner -->
        <v-alert
          type="warning"
          class="mb-4 rounded-lg d-flex align-center"
          density="compact"
          style="
            background: linear-gradient(45deg, #ff9800, #ff5722);
            color: white;
          "
        >
          <span class="font-weight-bold">
            {{ $t("GuessTheBalance.Dialogs.Start.Warning") }}
          </span>
        </v-alert>

        <!-- Main Message -->
        <v-card-text class="text-center">
          <span class="font-weight-bold text-h5">
            {{ $t("GuessTheBalance.Dialogs.Start.ConfirmMessage") }}
          </span>
        </v-card-text>

        <!-- Divider -->
        <v-divider class="my-4"></v-divider>

        <!-- Action Buttons -->
        <v-card-actions class="justify-end">
          <v-btn
            @click="startDialog = false"
            :disabled="loadingStart"
            variant="outlined"
            color="grey darken-2"
            class="px-5"
          >
            {{ $t("Buttons.Cancel") }}
          </v-btn>

          <v-btn
            :loading="loadingStart"
            @click="startGuessing()"
            class="px-5"
            style="
              background: linear-gradient(45deg, #e53935, #d32f2f);
              color: white;
            "
          >
            <v-icon left>mdi-check-circle</v-icon>
            {{ $t("Buttons.Confirm") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="playDialog"
      width="450"
      persistent
      style="
        font-family:
          Saira Condensed,
          sans-serif;
      "
    >
      <v-card color="primary" rounded="lg" class="pa-2">
        <v-card-text class="font-weight-bold">
          {{ $t("GuessTheBalance.Dialogs.Play.ConfirmMessage") }}
        </v-card-text>
        <template v-slot:actions>
          <v-spacer></v-spacer>

          <v-btn
            @click="playDialog = false"
            :disabled="loadingPlay"
            variant="outlined"
          >
            {{ $t("Buttons.Cancel") }}
          </v-btn>
          <v-btn
            @click="startPlaying()"
            :loading="loadingPlay"
            color="red"
            variant="elevated"
          >
            {{ $t("Buttons.Confirm") }}
          </v-btn>
        </template>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="doneDialog"
      width="450"
      persistent
      style="
        font-family:
          Saira Condensed,
          sans-serif;
      "
    >
      <v-card color="white" rounded="xl" class="pa-6" elevation="10">
        <!-- Warning Banner -->
        <v-alert
          type="warning"
          class="mb-4 rounded-lg d-flex align-center"
          density="compact"
          style="
            background: linear-gradient(45deg, #ff9800, #ff5722);
            color: white;
          "
        >
          <span class="font-weight-bold">
            {{ $t("GuessTheBalance.Dialogs.Done.Warning") }}
          </span>
        </v-alert>

        <!-- Main Message -->
        <v-card-text class="text-center">
          <span class="font-weight-bold text-h6">
            {{ $t("GuessTheBalance.Dialogs.Done.ConfirmMessage") }}
          </span>
        </v-card-text>

        <!-- Divider -->
        <v-divider class="my-4"></v-divider>

        <!-- Action Buttons -->
        <v-card-actions class="justify-end">
          <v-btn
            @click="doneDialog = false"
            :disabled="loadingDone"
            variant="outlined"
            color="grey darken-2"
            class="px-5"
          >
            {{ $t("Buttons.Cancel") }}
          </v-btn>

          <v-btn
            :loading="loadingDone"
            @click="donePlaying()"
            color="red"
            variant="elevated"
            class="px-5"
            style="
              background: linear-gradient(45deg, #e53935, #d32f2f);
              color: white;
            "
          >
            <v-icon left>mdi-check-circle</v-icon>
            {{ $t("Buttons.Confirm") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="deleteDialog"
      width="450"
      persistent
      style="
        font-family:
          Saira Condensed,
          sans-serif;
      "
    >
      <v-card color="primary" rounded="lg" class="pa-2">
        <v-card-text class="font-weight-bold">
          {{ $t("GuessTheBalance.Dialogs.Delete.ConfirmMessage") }}
        </v-card-text>
        <template v-slot:actions>
          <v-spacer></v-spacer>

          <v-btn
            @click="deleteDialog = false"
            :disabled="loadingDelete"
            variant="outlined"
          >
            {{ $t("Buttons.Cancel") }}
          </v-btn>
          <v-btn
            :loading="loadingDelete"
            @click="deleteGTB()"
            color="red"
            variant="elevated"
          >
            {{ $t("Buttons.Confirm") }}
          </v-btn>
        </template>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar"
      :timeout="2500"
      color="secondary"
      location="top"
    >
      <v-icon>mdi-information</v-icon>
      <span
        class="ml-2"
        style="
          font-family:
            Saira Condensed,
            sans-serif;
        "
        >{{ snackText }}</span
      >
    </v-snackbar>

    <GuessTheBalanceDialog ref="GuessTheBalanceDialog" @refresh="init()" />
    <ViewSlotsDetails ref="ViewSlotsDetails" />
    <EndingBalanceDialog ref="EndingBalanceDialog" @refresh="init()" />
    <ViewBalanceGuesses ref="ViewBalanceGuesses" />
  </div>
</template>

<script>
import axios from "axios";
import { defineComponent } from "vue";
import moment from "moment";
import { useTimer } from "vue-timer-hook";
import GuessTheBalanceDialog from "../Dialogs/GuessTheBalanceDialog.vue";
import ViewBalanceGuesses from "../Dialogs/Views/ViewBalanceGuesses.vue";
import EndingBalanceDialog from "../Dialogs/EndingBalanceDialog.vue";
import ViewSlotsDetails from "../Dialogs/Views/ViewSlotsDetails.vue";

export default defineComponent({
  components: {
    GuessTheBalanceDialog,
    ViewBalanceGuesses,
    ViewSlotsDetails,
    EndingBalanceDialog,
  },
  data() {
    return {
      gtbData: [],
      search: null,
      loading: false,
      headers: [
        {
          title: this.$t("GuessTheBalance.Table.BonusHuntId"),
          align: "start",
          sortable: true,
          key: "bonusHuntId.bonusHuntId",
        },
        {
          title: this.$t("GuessTheBalance.Table.Name"),
          align: "start",
          sortable: true,
          key: "bonusHuntId.name",
        },
        {
          title: this.$t("GuessTheBalance.Table.Required"),
          align: "start",
          sortable: true,
          key: "requiredPoints",
        },
        {
          title: this.$t("GuessTheBalance.Table.Timer"),
          align: "start",
          sortable: true,
          key: "timerDuration",
        },
        {
          title: this.$t("GuessTheBalance.Table.Status"),
          align: "start",
          sortable: true,
          key: "status",
        },
        {
          title: this.$t("GuessTheBalance.Table.CreatedAt"),
          align: "start",
          sortable: true,
          key: "createdAt",
        },
        {
          title: this.$t("GuessTheBalance.Table.Actions"),
          align: "end",
          sortable: false,
          key: "action",
        },
      ],

      snackbar: false,
      snackText: null,

      timerHasStarted: false,
      startDialog: false,
      playDialog: false,
      doneDialog: false,
      deleteDialog: false,
      loadingDelete: false,
      loadingDone: false,
      loadingPlay: false,
      loadingStart: false,
      itemToUpdate: [],
      itemToDelete: [],
    };
  },
  methods: {
    init() {
      const token = localStorage.getItem("abughazi-admin-accesstoken");
      if (token) {
        this.loading = true;
        axios
          .get(
            process.env.VUE_APP_SERVER_URL + "/api/admin/guess-the-balance",
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "x-admin-key": process.env.VUE_APP_X_ADMIN_KEY,
              },
              params: {
                page: 1,
                limit: 10000,
              },
            },
          )
          .then((res) => {
            if (res) {
              this.gtbData = res.data.gtbs;
              this.loading = false;
            }
          })
          .catch((error) => {
            if (error.response) {
              if (error.response.status === 401) {
                localStorage.removeItem("vuex");
                localStorage.removeItem("abughazi-admin-accesstoken");
                window.location.reload();
              } else {
                console.log(`Error: ${error.response.status}`);
              }
            } else if (error.request) {
              console.log("No response received", error.request);
            } else {
              console.log("Error", error.message);
            }
          });
      } else {
        localStorage.removeItem("vuex");
        localStorage.removeItem("abughazi-admin-accesstoken");
        window.location.reload();
      }
    },
    timer(data) {
      const timer = useTimer(
        moment(data.timerTimeStarted).utc().add(data.timerDuration, "minutes"),
      );
      return timer;
    },
    hasOpenSlots(gtb) {
      const res =
        gtb.slots &&
        gtb.slots.length > 0 &&
        gtb.slots.some((slot) => slot.status !== "done");
      return res;
    },
    showAddDialog() {
      this.$refs.GuessTheBalanceDialog.initAdd();
    },
    showEditDialog(data) {
      this.$refs.GuessTheBalanceDialog.initUpdate(data);
    },
    startTimer(data) {
      this.timerHasStarted = true;
      this.startDialog = true;
      this.itemToUpdate = data;
    },
    showDoneDialog(data) {
      this.doneDialog = true;
      this.itemToUpdate = data;
    },
    donePlaying() {
      this.loadingDone = false;
      this.doneDialog = false;

      console.log(this.itemToUpdate);

      this.$refs.EndingBalanceDialog.initEnd(this.itemToUpdate);
    },
    showPlayDialog(data) {
      this.playDialog = true;
      this.itemToUpdate = data;
    },
    showDeleteDialog(data) {
      this.itemToDelete = data;
      this.deleteDialog = true;
    },
    viewBalanceDetail(data) {
      this.$refs.ViewBalanceGuesses.init(data);
    },
    viewSlots(data) {
      this.$refs.ViewSlotsDetails.init(data);
    },
    startGuessing() {
      this.startDialog = false;
      this.loadingStart = true;
      const token = localStorage.getItem("abughazi-admin-accesstoken");
      axios
        .post(
          process.env.VUE_APP_SERVER_URL +
            "/api/admin/guess-the-balance/start-timer/" +
            this.itemToUpdate._id,
          1,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "x-admin-key": process.env.VUE_APP_X_ADMIN_KEY,
            },
          },
        )
        .then((res) => {
          if (res.data) {
            this.loadingStart = false;
            this.startDialog = false;
            this.init();
            this.snackText = this.$t("GuessTheBalance.Messages.Started");
            this.snackbar = true;
          }
        })
        .catch((error) => {
          if (error.response) {
            if (error.response.status === 401) {
              localStorage.removeItem("vuex");
              localStorage.removeItem("abughazi-admin-accesstoken");
              window.location.reload();
            } else {
              console.log(`Error: ${error.response.status}`);
            }
          } else if (error.request) {
            console.log("No response received", error.request);
          } else {
            console.log("Error", error.message);
          }

          this.loadingStart = false;
          this.startDialog = false;
          this.snackText =
            this.$t("GuessTheBalance.Messages.UnableToStartTimer") +
            (error.response?.data?.message ||
              this.$t("GuessTheBalance.Messages.CheckSlotAssociated"));
          this.snackbar = true;
        });
    },
    startPlaying() {
      this.loadingPlay = true;
      const token = localStorage.getItem("abughazi-admin-accesstoken");
      const formData = {
        id: this.itemToUpdate._id,
        status: "ongoing",
      };
      axios
        .post(
          process.env.VUE_APP_SERVER_URL +
            "/api/admin/guess-the-balance/update",
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "x-admin-key": process.env.VUE_APP_X_ADMIN_KEY,
            },
          },
        )
        .then((res) => {
          if (res) {
            this.loadingPlay = false;
            this.playDialog = false;
            this.init();
            this.snackText = this.$t("GuessTheBalance.Messages.DetailsUpdated");
            this.snackbar = true;
          }
        })
        .catch((error) => {
          if (error.response) {
            console.log("Error", error.message);
          }
        });
    },
    deleteGTB() {
      this.loadingDelete = true;
      const token = localStorage.getItem("abughazi-admin-accesstoken");
      axios
        .delete(
          process.env.VUE_APP_SERVER_URL +
            "/api/admin/guess-the-balance/" +
            this.itemToDelete._id,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "x-admin-key": process.env.VUE_APP_X_ADMIN_KEY,
            },
          },
        )
        .then((res) => {
          if (res) {
            this.loadingDelete = false;
            this.deleteDialog = false;
            this.init();
            this.snackText = this.$t(
              "GuessTheBalance.Messages.DeletedSuccessfully",
            );
            this.snackbar = true;
          }
        })
        .catch((error) => {
          if (error.response) {
            console.log("Error", error.message);
          }
        });
    },
  },
  mounted() {
    this.init();
  },
});
</script>

<style scoped>
.guide-panel {
  background-color: #f5f5f5; /* Light gray */
  border-left: 5px solid #d32f2f; /* Red left border */
  color: #212121;
  transition: background 0.3s ease;
}
.guide-panel-content {
  border-radius: 10px;
}
.guide-panel:hover {
  background-color: #e0e0e0;
}
</style>
